import { queryClient } from '@/client/query';
import Loading from '@/components/loading/loading';
import useAnalyticsCalls from '@/hooks/use-analytics';
import useGetMe from '@/queries/me.queries';
import { GetMeResponse } from '@/repositories/me.repository';
import datadogService from '@/services/datadog-rum.service';
import { useFeatureFlags } from '@/services/feature-flag-provider';
import { PropsWithChildren, createContext, useContext, useEffect } from 'react';

type MeValue = {
  data: GetMeResponse | null | undefined;
  refetch: () => void;
};

const defaultValue = {
  data: {},
  refetch: () => {
    //
  },
} as unknown as MeValue;

const MeContext = createContext<MeValue>(defaultValue);

export function useMe() {
  const context = useContext(MeContext);

  if (!context) {
    throw new Error('useMe must be used within a MeProvider');
  }

  return context;
}

export function MeProvider(props: PropsWithChildren) {
  const { data, isLoading, refetch: refetchMe } = useGetMe();
  const analytics = useAnalyticsCalls();
  const { setUser } = useFeatureFlags();

  useEffect(() => {
    const identifyUser = async () => {
      if (data?.account?.id && data?.account?.credential) {
        // Identify user with analytics
        analytics.callIdentifyEmail(data.account.id, data.account.credential);
        // Set user context for Datadog RUM
        datadogService.setUser(data.account.id);
        // Set user context for feature flags
        await setUser({
          user_id: data.account.id,
          device_id: data.account.id, // Use user ID as device ID for consistency
          user_properties: {
            email: data.account.credential,
          },
        });
      }
    };

    identifyUser();
  }, [data?.account?.id, data?.account?.credential, analytics, setUser]);

  function refetch() {
    queryClient.invalidateQueries({ queryKey: ['me', 'get'] }).then(() => refetchMe());
  }

  return isLoading ? <Loading /> : <MeContext.Provider value={{ data, refetch }}>{props.children}</MeContext.Provider>;
}
